import { useLayoutEffect, useRef, useState } from 'react';

import { frameRates, type FrameRate } from '@cartwhl/common/types';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Input from '@cartwhl/ui/atoms/Input';
import Select from '@cartwhl/ui/atoms/Select';
import Text from '@cartwhl/ui/base/Text';

import type { ExportType } from './exportOptionsTypes';

/**
 * Job name is driven by a global store; re-renders from unrelated store fields
 * can reset caret position in a purely controlled <input>. Keep a local draft
 * while focused and only sync from props when the field is not focused.
 */
function JobNameInput({
  jobName,
  jobNameRequired,
  jobNameError,
  onJobNameChange,
}: {
  jobName: string;
  jobNameRequired: boolean;
  jobNameError: boolean;
  onJobNameChange: (value: string) => void;
}) {
  const focused = useRef(false);
  const jobNamePropRef = useRef(jobName);
  jobNamePropRef.current = jobName;

  const [draft, setDraft] = useState(jobName);

  useLayoutEffect(() => {
    if (!focused.current) {
      setDraft(jobName);
    }
  }, [jobName]);

  return (
    <>
      <Text
        variant="studio/body/md"
        className={jobNameError ? 'text-red' : undefined}
      >
        Job name
        {!jobNameRequired ? (
          <span className="text-gray-60"> (optional)</span>
        ) : null}
      </Text>
      <Input
        required={jobNameRequired}
        name="jobName"
        placeholder="Ex. Fighting moves"
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          onJobNameChange(v);
        }}
        onFocus={() => {
          focused.current = true;
        }}
        onBlur={() => {
          focused.current = false;
          setDraft(jobNamePropRef.current);
        }}
        className="w-full transition-none"
      />
    </>
  );
}

export type ExportOptionsSectionProps = {
  jobName: string;
  /** When true, "(optional)" is hidden and the field is required. */
  jobNameRequired?: boolean;
  /** When true, the Job name label uses error styling (e.g. after a blocked submit). */
  jobNameError?: boolean;
  fileType: ExportType;
  frameRate: FrameRate;
  keyframeSimplification: 'none' | 'simplify';
  includeMesh: boolean;
  onJobNameChange: (value: string) => void;
  onFileTypeChange: (value: ExportType) => void;
  onFrameRateChange: (value: FrameRate) => void;
  onKeyframeSimplificationChange: (value: 'none' | 'simplify') => void;
  onIncludeMeshChange: (value: boolean) => void;
  facialCapture?: boolean;
  onFacialCaptureChange?: (value: boolean) => void;
};

const FILE_TYPE_ITEMS = [
  { label: 'FBX', value: 'FBX' },
  { label: 'glTF', value: 'GLB' },
  { label: 'BVH', value: 'BVH' },
  { label: 'Maya', value: 'MA' },
];

const SOFTWARE_ITEMS = [
  { label: 'Maya', value: 'FBX_MAYA' },
  { label: 'Blender', value: 'FBX_BLENDER' },
  { label: 'Unreal', value: 'FBX_UNREAL' },
  { label: 'Roblox', value: 'FBX_ROBLOX' },
];

export default function ExportOptionsSection({
  jobName,
  jobNameRequired = false,
  jobNameError = false,
  fileType,
  frameRate,
  keyframeSimplification,
  includeMesh,
  onJobNameChange,
  onFileTypeChange,
  onFrameRateChange,
  onKeyframeSimplificationChange,
  onIncludeMeshChange,
  facialCapture,
  onFacialCaptureChange,
}: ExportOptionsSectionProps) {
  const displayFileType = fileType.startsWith('FBX') ? 'FBX' : fileType;

  const handleFileTypeChange = (value: string) => {
    if (value === 'FBX') {
      onFileTypeChange('FBX_UNREAL');
    } else {
      onFileTypeChange(value as ExportType);
    }
  };

  const handleSoftwareChange = (value: string) => {
    onFileTypeChange(value as ExportType);
  };

  const showSoftware = displayFileType === 'FBX';
  const showSplineSimplification =
    displayFileType === 'FBX' || fileType === 'MA' || fileType === 'MB';
  const showCharacter = displayFileType === 'FBX' || fileType === 'GLB';

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-2.5">
          <JobNameInput
            jobName={jobName}
            jobNameRequired={jobNameRequired}
            jobNameError={jobNameError}
            onJobNameChange={onJobNameChange}
          />
        </div>

        <div className="flex flex-col gap-2.5">
          <Text variant="studio/body/md">File type</Text>
          <Select
            value={displayFileType}
            onValueChange={handleFileTypeChange}
            items={FILE_TYPE_ITEMS}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {showSoftware ? (
          <div className="flex flex-col gap-2.5">
            <Text variant="studio/body/md">Software</Text>
            <Select
              value={fileType}
              onValueChange={handleSoftwareChange}
              items={SOFTWARE_ITEMS}
            />
          </div>
        ) : (
          <div />
        )}

        <div className="flex flex-col gap-2.5">
          <Text variant="studio/body/md">Frame rate</Text>
          <Select
            value={frameRate}
            onValueChange={(value) => onFrameRateChange(parseInt(value) as FrameRate)}
            items={frameRates.map((rate) => ({
              label: rate.toString(),
              value: rate,
            }))}
          />
        </div>
      </div>

      {(showSplineSimplification || showCharacter) && (
        <div className="grid grid-cols-2 items-stretch gap-6">
          {showSplineSimplification ? (
            <div className="flex h-full min-h-0 min-w-0 flex-col gap-2.5">
              <Text variant="studio/body/md">Spline simplification</Text>
              <div className="flex min-h-0 flex-1 items-center">
                <div className="min-w-0 w-full">
                  <Select
                    value={keyframeSimplification}
                    onValueChange={(value) =>
                      onKeyframeSimplificationChange(value as 'none' | 'simplify')
                    }
                    items={[
                      { label: 'None', value: 'none' },
                      { label: 'Rebuild curves', value: 'simplify' },
                    ]}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div />
          )}

          {showCharacter ? (
            <div className="flex h-full min-h-0 min-w-0 flex-col gap-2.5">
              <Text variant="studio/body/md">Character</Text>
              <div className="flex min-h-0 flex-1 items-center justify-start">
                <Checkbox
                  id="include-mesh"
                  checked={includeMesh}
                  label="Include mesh"
                  variant="checkbox"
                  color="settings"
                  align="center"
                  labelClassName="!text-gray-90 dark:!text-gray-30"
                  setChecked={onIncludeMeshChange}
                />
              </div>
            </div>
          ) : (
            <div />
          )}
        </div>
      )}

      {onFacialCaptureChange && (
        <>
          <div className="border-gray-30 dark:border-gray-70 h-px w-full border-b" />

          <div className="flex flex-col gap-4">
            <Text variant="studio/body/medium/sm">Processing options</Text>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-3">
                <Checkbox
                  id="facial-capture"
                  checked={facialCapture ?? false}
                  label="Facial capture"
                  variant="checkbox"
                  color="settings"
                  setChecked={onFacialCaptureChange}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <div className="border-gray-30 dark:border-gray-70 flex flex-col gap-4 border-t py-4">
        <div className="flex justify-between gap-4">
          <Text variant="studio/body/medium/sm" className="col-span-1">
            Orientation
          </Text>

          <div className="col-span-1 flex items-center gap-2">
            <Text className="text-gray-60" variant="studio/body/sm">
              Forward:
            </Text>
            <Text className="text-gray-60" variant="studio/body/sm">
              -Z Forward
            </Text>
          </div>

          <div className="col-span-1 flex items-center gap-2">
            <Text className="text-gray-60" variant="studio/body/sm">
              Up:
            </Text>
            <Text className="text-gray-60" variant="studio/body/sm">
              Y Up
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
